import React, { useMemo, useState } from 'react';
import { Button, ButtonGroup, Dialog, NonIdealState, Spinner, Tag } from '@blueprintjs/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast as message } from 'sonner';
import { supabaseClient, supabaseCoreClient } from '../utils/supabase';
import { useGlobalMetadata } from '../utils/providers';
import { isValidEmail, normalizeEmail } from '../utils/validation';
import type { DynamicRecord } from '../types/api';
import type { EnrichedTableField } from '../types/metadata';
import {
  DEFAULT_EMAIL_DOMAIN,
  EMAIL_COMPANIES,
  type EmailCompanyDomain,
} from '../utils/emailCompanies';

interface GmailRecordEmailTabProps {
  tableName: string;
  recordId: number;
}

interface GmailMessageRow {
  id: number;
  gmail_thread_id: string | null;
  gmail_message_id: string | null;
  mailbox_email: string | null;
  normalized_subject: string | null;
  from_email: string | null;
  to_emails: string[] | null;
  sent_at: string | null;
  internal_date: string | null;
  snippet: string | null;
  body_text: string | null;
  body_html: string | null;
  payload_fetched_at: string | null;
  payload_fetch_error: string | null;
  attachment_count?: number | null;
  attachments?: GmailAttachmentRow[] | null;
}

interface GmailAttachmentRow {
  id: number;
  gmail_message: number;
  filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  fetched_at: string | null;
}

const GmailRecordEmailTab: React.FC<GmailRecordEmailTabProps> = ({ tableName, recordId }) => {
  const queryClient = useQueryClient();
  const { data: metadata } = useGlobalMetadata();
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<number | null>(null);
  const [keepAliveRequested, setKeepAliveRequested] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [company, setCompany] = useState<EmailCompanyDomain>(DEFAULT_EMAIL_DOMAIN);

  const tableInfo = metadata?.tableData?.byName?.[tableName];
  const emailFieldNames = useMemo(() => {
    if (!tableInfo?.id) return [];
    const keepAliveMap = metadata?.globalSettingsData?.gmail_keep_alive_table_field_map || {};
    const configuredFieldIds = keepAliveMap[String(tableInfo.id)] || [];
    const configuredNames = configuredFieldIds
      .map((fieldId) => metadata?.fieldData?.byId?.[fieldId] as EnrichedTableField | undefined)
      .filter((field): field is EnrichedTableField =>
        Boolean(field?.name && field.table_info === tableInfo.id && field.is_email),
      )
      .map((field) => field.name as string);
    return configuredNames.length ? configuredNames : ['email'];
  }, [metadata, tableInfo?.id]);

  const { data: record, isLoading: recordLoading } = useQuery<DynamicRecord | null>({
    queryKey: ['gmail-record-email-source', tableName, recordId, emailFieldNames],
    enabled: !!tableName && !!recordId && emailFieldNames.length > 0,
    queryFn: async () => {
      const { data, error } = await (supabaseClient as any)
        .from(tableName)
        .select('*')
        .eq('id', recordId)
        .single();
      if (error) throw error;
      return data as DynamicRecord;
    },
  });

  const recordEmail = useMemo(() => {
    if (!record) return null;
    for (const fieldName of emailFieldNames) {
      const value = record[fieldName];
      if (isValidEmail(value)) return normalizeEmail(value);
    }
    return null;
  }, [emailFieldNames, record]);

  React.useEffect(() => {
    if (!recordEmail) return;
    setKeepAliveRequested(false);
    void supabaseClient.functions
      .invoke('gmail-keep-alive', { body: { email: recordEmail } })
      .then(({ error }) => {
        if (error) throw error;
        setKeepAliveRequested(true);
        void queryClient.invalidateQueries({ queryKey: ['gmail-record-messages', recordEmail] });
      })
      .catch((error) => {
        console.warn('Failed to refresh Gmail keep-alive from email tab:', error);
      });
  }, [queryClient, recordEmail]);

  const { data: gmailMessages = [], isLoading: messagesLoading } = useQuery<GmailMessageRow[]>({
    queryKey: ['gmail-record-messages', recordEmail, company],
    enabled: !!recordEmail,
    queryFn: async () => {
      if (!recordEmail) return [];
      const { data, error } = await supabaseCoreClient.rpc(
        'search_gmail_messages_for_participants',
        { p_emails: [recordEmail], p_limit: 100, p_mailbox_domain: company },
      );
      if (error) throw error;
      return (data || []) as GmailMessageRow[];
    },
  });

  const attachmentsByMessageId = useMemo(() => {
    const grouped = new Map<number, GmailAttachmentRow[]>();
    for (const gmailMessage of gmailMessages) {
      const attachments = Array.isArray(gmailMessage.attachments)
        ? gmailMessage.attachments
        : [];
      grouped.set(gmailMessage.id, attachments);
    }
    return grouped;
  }, [gmailMessages]);

  const threads = useMemo(
    () => groupMessagesByThread(gmailMessages, attachmentsByMessageId),
    [attachmentsByMessageId, gmailMessages],
  );
  const selectedThread = useMemo(
    () => threads.find((thread) => thread.threadId === selectedThreadId) || null,
    [selectedThreadId, threads],
  );

  const handleDownloadAttachment = async (attachment: GmailAttachmentRow) => {
    setDownloadingAttachmentId(attachment.id);
    try {
      const { data, error } = await supabaseClient.functions.invoke('fetch-gmail-attachment', {
        body: { attachment_ids: [attachment.id], limit: 1 },
      });
      if (error) throw error;

      const result = data?.results?.[0];
      if (!result?.ok || !result.data_base64) {
        throw new Error(result?.error || 'Attachment fetch failed');
      }

      downloadBase64File(
        result.data_base64,
        result.mime_type || attachment.mime_type || 'application/octet-stream',
        result.filename || attachment.filename || `gmail-attachment-${attachment.id}`,
      );
      await queryClient.invalidateQueries({ queryKey: ['gmail-record-messages', recordEmail] });
    } catch (error) {
      const text = error instanceof Error ? error.message : String(error);
      message.error(`Failed to download attachment: ${text}`);
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  // Region Capital's inbox is kept apart from Skyline's (REG-817).
  const companySwitch = (
    <ButtonGroup className="gmail-record-email-company" aria-label="Company">
      {EMAIL_COMPANIES.map((option) => (
        <Button
          key={option.key}
          small
          active={company === option.domain}
          onClick={() => {
            setCompany(option.domain);
            setSelectedThreadId(null);
          }}
        >
          {option.label}
        </Button>
      ))}
    </ButtonGroup>
  );

  if (recordLoading) return <Spinner />;
  if (!recordEmail) return <NonIdealState icon="envelope" title="No email address" />;
  if (messagesLoading) {
    return (
      <div className="gmail-record-email-tab">
        <div className="gmail-record-email-toolbar">{companySwitch}</div>
        <Spinner />
      </div>
    );
  }
  if (gmailMessages.length === 0) {
    return (
      <div className="gmail-record-email-tab">
        <div className="gmail-record-email-toolbar">{companySwitch}</div>
        <NonIdealState
        icon="inbox"
        title="No synced email yet"
        description={
          keepAliveRequested
            ? `We requested email sync for ${recordEmail}. If matching mail exists in synced company inboxes, it will appear here after the next metadata and payload pass.`
            : `Preparing to sync email with ${recordEmail}.`
        }
        />
      </div>
    );
  }

  return (
    <div className="gmail-record-email-tab">
      <div className="gmail-record-email-toolbar">
        {companySwitch}
        <Tag minimal>{recordEmail}</Tag>
        <Tag minimal>{threads.length} threads</Tag>
        <Tag minimal>{gmailMessages.length} messages</Tag>
      </div>
      <div className="gmail-record-email-list">
        {threads.map((thread) => (
          <button
            key={thread.threadId}
            type="button"
            className="gmail-record-email-row"
            onClick={() => setSelectedThreadId(thread.threadId)}
          >
            <div className="gmail-record-email-row-sender">
              {thread.senders.join(', ') || 'Unknown sender'}
            </div>
            <div className="gmail-record-email-row-main">
              <div className="gmail-record-email-row-subject">
                {thread.subject || '(No subject)'}
              </div>
              <div className="gmail-record-email-row-snippet">
                {thread.preview || 'Body has not been fetched yet.'}
              </div>
            </div>
            <div className="gmail-record-email-row-badges">
              {thread.attachmentCount > 0 && <Tag minimal icon="paperclip">{thread.attachmentCount}</Tag>}
              {thread.messages.length > 1 && <Tag minimal>{thread.messages.length}</Tag>}
            </div>
            <div className="gmail-record-email-row-date">{formatShortDate(thread.latestDate)}</div>
          </button>
        ))}
      </div>

      <Dialog
        isOpen={!!selectedThread}
        onClose={() => setSelectedThreadId(null)}
        title={selectedThread?.subject || 'Email thread'}
        className="gmail-record-email-dialog"
      >
        {selectedThread && (
          <div className="gmail-record-email-dialog-body">
            <div className="gmail-record-email-dialog-meta">
              <Tag minimal>{selectedThread.messages.length} messages</Tag>
              <Tag minimal>{selectedThread.mailboxEmails.join(', ')}</Tag>
            </div>

            {selectedThread.attachments.length > 0 && (
              <div className="gmail-record-email-thread-attachments">
                <div className="gmail-record-email-section-title">Thread attachments</div>
                <AttachmentButtons
                  attachments={selectedThread.attachments}
                  downloadingAttachmentId={downloadingAttachmentId}
                  onDownload={handleDownloadAttachment}
                />
              </div>
            )}

            <div className="gmail-record-email-dialog-messages">
              {selectedThread.messages.map((gmailMessage) => (
                <div key={gmailMessage.id} className="gmail-record-email-message">
                  <div className="gmail-record-email-card-header">
                    <div>
                      <div className="gmail-record-email-message-from">
                        {gmailMessage.from_email || 'Unknown sender'}
                      </div>
                      <div className="gmail-record-email-meta">
                        To: {formatRecipients(gmailMessage.to_emails)}
                      </div>
                      <div className="gmail-record-email-meta">
                        Mailbox: {gmailMessage.mailbox_email || 'Unknown'}
                      </div>
                    </div>
                    <div className="gmail-record-email-date">
                      {formatDate(gmailMessage.sent_at || gmailMessage.internal_date)}
                    </div>
                  </div>

                  {gmailMessage.body_html ? (
                    <div
                      className="gmail-record-email-html"
                      dangerouslySetInnerHTML={{ __html: gmailMessage.body_html }}
                    />
                  ) : (
                    <div className="gmail-record-email-snippet">
                      {gmailMessage.body_text || gmailMessage.snippet || 'Body has not been fetched yet.'}
                    </div>
                  )}

                  {gmailMessage.payload_fetch_error && (
                    <div className="gmail-record-email-error">{gmailMessage.payload_fetch_error}</div>
                  )}

                  {(attachmentsByMessageId.get(gmailMessage.id) || []).length > 0 && (
                    <div className="gmail-record-email-attachments">
                      <AttachmentButtons
                        attachments={attachmentsByMessageId.get(gmailMessage.id) || []}
                        downloadingAttachmentId={downloadingAttachmentId}
                        onDownload={handleDownloadAttachment}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

interface AttachmentButtonsProps {
  attachments: GmailAttachmentRow[];
  downloadingAttachmentId: number | null;
  onDownload: (attachment: GmailAttachmentRow) => void;
}

const AttachmentButtons: React.FC<AttachmentButtonsProps> = ({
  attachments,
  downloadingAttachmentId,
  onDownload,
}) => (
  <>
    {attachments.map((attachment) => (
      <Button
        key={attachment.id}
        small
        icon="download"
        loading={downloadingAttachmentId === attachment.id}
        onClick={() => onDownload(attachment)}
      >
        {attachment.filename || `Attachment ${attachment.id}`}
      </Button>
    ))}
  </>
);

function formatRecipients(recipients: string[] | null): string {
  if (!recipients?.length) return 'Unknown recipients';
  if (recipients.length <= 3) return recipients.join(', ');
  return `${recipients.slice(0, 3).join(', ')} +${recipients.length - 3}`;
}

function formatDate(date: string | null): string {
  if (!date) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date));
}

function formatShortDate(date: string | null): string {
  if (!date) return '';
  const parsed = new Date(date);
  const now = new Date();
  const sameYear = parsed.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(undefined, sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' }
  ).format(parsed);
}

function downloadBase64File(base64: string, mimeType: string, filename: string): void {
  const normalized = base64.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

interface GmailThreadGroup {
  threadId: string;
  subject: string | null;
  latestDate: string | null;
  mailboxEmails: string[];
  senders: string[];
  preview: string | null;
  attachments: GmailAttachmentRow[];
  attachmentCount: number;
  messages: GmailMessageRow[];
}

function groupMessagesByThread(
  messages: GmailMessageRow[],
  attachmentsByMessageId?: Map<number, GmailAttachmentRow[]>,
): GmailThreadGroup[] {
  const groups = new Map<string, GmailThreadGroup>();

  for (const message of messages) {
    const threadId = message.gmail_thread_id || `message:${message.id}`;
    const existing = groups.get(threadId) || {
      threadId,
      subject: message.normalized_subject,
      latestDate: null,
      mailboxEmails: [],
      senders: [],
      preview: null,
      attachments: [],
      attachmentCount: 0,
      messages: [],
    };
    existing.messages.push(message);
    if (!existing.subject && message.normalized_subject) existing.subject = message.normalized_subject;
    if (!existing.preview) existing.preview = message.body_text || message.snippet || null;
    const date = message.sent_at || message.internal_date;
    if (date && (!existing.latestDate || new Date(date).getTime() > new Date(existing.latestDate).getTime())) {
      existing.latestDate = date;
    }
    if (message.mailbox_email && !existing.mailboxEmails.includes(message.mailbox_email)) {
      existing.mailboxEmails.push(message.mailbox_email);
    }
    if (message.from_email && !existing.senders.includes(message.from_email)) {
      existing.senders.push(message.from_email);
    }
    const attachments = attachmentsByMessageId?.get(message.id) || [];
    existing.attachments.push(...attachments);
    existing.attachmentCount += attachments.length || message.attachment_count || 0;
    groups.set(threadId, existing);
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      messages: group.messages.sort((a, b) =>
        new Date(a.sent_at || a.internal_date || 0).getTime() -
        new Date(b.sent_at || b.internal_date || 0).getTime(),
      ),
    }))
    .sort((a, b) =>
      new Date(b.latestDate || 0).getTime() - new Date(a.latestDate || 0).getTime(),
    );
}

export default GmailRecordEmailTab;
